import moment from "moment";
import { Card, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    DateTimePicker,
    FormField,
    InputHtmlEditor,
    SelectMultiLanguage,
    Switch,
    Typography,
    IPropDetailContainer,
    useCommonComponentContext,
} from "@maysoft/common-component-react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import UserService from "../../services/identity/User.service";
import GroupService from "../../services/identity/Group.service";
import NotificationService, { IDateCreateMsg, ISendUsers } from "../../services/message/Notification.service";

import { getValueNumber1000 } from "./list";
import { ICodename } from "../../commons/interfaces";
import { Constants } from "../../constants/constants";
import { IsTrue, MessageTargetType, MessageType, Mode } from "../../constants/enums";

type IError = { [k in keyof IDateCreateMsg]?: string };

interface ITargetMsg {
    groupIds?: string[];
    users?: ISendUsers[];
}

const emptyData: IDateCreateMsg = {
    type: MessageType.PushNotification,
    organizationId: "0",
    clientId: undefined,
    from: undefined,
    realTime: true,
    requestType: 0,
    requestId: undefined,
    redirectUrl: undefined,
    sendTime: moment().unix(),
    title: { value: { vi: undefined } },
    content: { value: { vi: undefined } },
    sendUsers: [],
    groups: [],
};

const NotificationEditContainer = (props: IPropDetailContainer) => {
    const Strings = useStrings();

    const { language, clientId, userInfo, listMultiLanguage, onError, onSuccess, onShowLoading, onHideLoading } = useCommonComponentContext();

    const [isEdit, setIsEdit] = useState(false);
    const [error, setError] = useState<IError>({} as IError);
    const [data, setData] = useState<IDateCreateMsg>({ ...emptyData });

    const [userList, setUserList] = useState<ICodename[]>([]);
    const [groupList, setGroupList] = useState<ICodename[]>([]);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(Constants.DefaultLanguage);
    const [dataToResponse, setDataToResponse] = useState<ITargetMsg>({ users: [], groupIds: [] });
    const [dataCcResponse, setDataCcResponse] = useState<ITargetMsg>({ users: [], groupIds: [] });
    const [dataBccResponse, setDataBccResponse] = useState<ITargetMsg>({ users: [], groupIds: [] });

    const messageTypeList = [
        {
            code: MessageType.PushNotification,
            name: Strings.Common.NOTIFICATION,
        },
    ];

    useEffect(() => {
        setSelectedLanguage(language);
    }, [language]);

    useEffect(() => {
        if (!Helpers.isNullOrEmpty(userInfo?.userProfile?.organizationId)) {
            onStarted();
        }
    }, [props.idDetail, userInfo?.userProfile?.organizationId]);

    const onStarted = async () => {
        try {
            onShowLoading();

            const userListTemp: ICodename[] = [];
            const resultUser = await UserService.getPaged(
                Helpers.handleFormatParams({
                    pageNumber: 1,
                    pageSize: 99999,
                    clientId: clientId,
                    organizationId: userInfo?.userProfile?.organizationId,
                })
            );
            [...(resultUser?.items || [])].forEach((item: any) => {
                userListTemp.push({
                    code: item.id,
                    name: item.organizationUserProfile?.firstName,
                    detail: {
                        email: item?.organizationUserProfile?.email,
                        phoneNumber: item?.organizationUserProfile?.phoneNumber,
                    },
                });
            });

            const userGroupTemp: ICodename[] = [];
            const resultGroup = await GroupService.getAll(
                Helpers.handleFormatParams({
                    listStatus: [1],
                    clientId: clientId,
                    organizationId: userInfo?.userProfile?.organizationId,
                })
            );
            [...(resultGroup || [])].forEach((item: any) => {
                userGroupTemp.push({
                    code: item.id,
                    name: Helpers.renderValueByLanguage({ data: item.name, currentLanguage: language }),
                    detail: {
                        type: item.type,
                        gene: item.gene,
                    },
                });
            });

            setUserList(userListTemp);
            setGroupList(userGroupTemp);

            if (!Helpers.isNullOrEmpty(props.idDetail)) {
                const result = await NotificationService.getDetailNotification(props.idDetail);

                let newUsersTo: ISendUsers[] = [];
                [...(result?.toResponse?.users || [])].forEach((item) => {
                    newUsersTo.push({
                        email: item?.user?.email,
                        userId: item?.user?.identityId,
                        phoneNumber: item?.user?.phoneNumber,
                    });
                });

                let newUsersCc: ISendUsers[] = [];
                [...(result?.ccResponse?.users || [])].forEach((item) => {
                    newUsersCc.push({
                        email: item?.user?.email,
                        userId: item?.user?.identityId,
                        phoneNumber: item?.user?.phoneNumber,
                    });
                });

                let newUsersBcc: ISendUsers[] = [];
                [...(result?.bccResponse?.users || [])].forEach((item) => {
                    newUsersBcc.push({
                        email: item?.user?.email,
                        userId: item?.user?.identityId,
                        phoneNumber: item?.user?.phoneNumber,
                    });
                });

                setDataToResponse({
                    users: newUsersTo,
                    groupIds: [...(result?.toResponse?.groupIds || [])],
                });

                setDataCcResponse({
                    users: newUsersCc,
                    groupIds: [...(result?.ccResponse?.groupIds || [])],
                });

                setDataBccResponse({
                    users: newUsersBcc,
                    groupIds: [...(result?.bccResponse?.groupIds || [])],
                });

                setData({
                    clientId: result?.clientId,
                    organizationId: result?.organizationId,

                    type: result?.type,
                    from: result?.from,
                    sendTime: result?.sendTime,
                    requestId: result?.requestId,
                    redirectUrl: result?.redirectUrl,
                    requestType: result?.requestType,
                    realTime: result?.readStatus === IsTrue.True,
                    title: result?.title || { value: { vi: undefined } },
                    content: result?.content || { value: { vi: undefined } },
                    sendUsers: [],
                    groups: [],
                });
            } else {
                setDataToResponse({ users: [], groupIds: [] });
                setDataCcResponse({ users: [], groupIds: [] });
                setDataBccResponse({ users: [], groupIds: [] });

                setData({
                    clientId: clientId,
                    organizationId: userInfo?.userProfile?.organizationId,

                    type: MessageType.PushNotification,
                    sendTime: moment().unix(),
                    from: undefined,
                    realTime: true,
                    requestType: 0,
                    requestId: undefined,
                    redirectUrl: undefined,
                    title: { value: { vi: undefined } },
                    content: { value: { vi: undefined } },
                    sendUsers: [],
                    groups: [],
                });
            }
        } catch (error: any) {
            onHideLoading();
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            onHideLoading();
        }
    };

    const handleCheckValidate = () => {
        let isValid = true;
        const newError = { ...error };
        let keys = ["type", "title", "content"];

        keys.forEach((key) => {
            let objectKey = key as keyof IDateCreateMsg;
            if ((key === "title" || key === "content") && Helpers.isNullOrEmpty(data?.[key]?.value?.[language])) {
                isValid = false;
                newError[key] = Strings.Common.REQUIRED;
            } else if (Helpers.isNullOrEmpty(data?.[objectKey])) {
                isValid = false;
                newError[objectKey] = Strings.Common.REQUIRED;
            }
        });

        if (data?.realTime === true) {
            if (Helpers.isNullOrEmpty(data?.sendTime) || data?.sendTime === 0) {
                isValid = false;
                newError["sendTime"] = Strings.Common.REQUIRED;
            }
        }

        if (dataToResponse?.groupIds?.length === 0 && dataToResponse?.users?.length === 0) {
            isValid = false;
            onError(Strings.NOTIFICATION.REQUIRED_GROUP_RECEIVER_OR_RECEIVER);
        }

        if (!isValid) {
            setError(newError);
        }

        return isValid;
    };

    const handleSubmit = async () => {
        if (!handleCheckValidate()) return;

        try {
            onShowLoading();

            const sendTime = data?.realTime === true ? moment().unix() : data?.sendTime;

            const newSendGroups: { type: number; groupIds: string[] }[] = [];
            const newSendUsers: { type: number; sendUsers: ISendUsers[] }[] = [];

            if (dataToResponse?.users?.length > 0) {
                newSendUsers.push({
                    type: MessageTargetType.To,
                    sendUsers: dataToResponse?.users || [],
                });
            }
            if (dataCcResponse?.users?.length > 0) {
                newSendUsers.push({
                    type: MessageTargetType.Cc,
                    sendUsers: dataCcResponse?.users || [],
                });
            }
            if (dataBccResponse?.users?.length > 0) {
                newSendUsers.push({
                    type: MessageTargetType.Bcc,
                    sendUsers: dataBccResponse?.users || [],
                });
            }

            if (dataToResponse?.groupIds?.length > 0) {
                newSendGroups.push({
                    type: MessageTargetType.To,
                    groupIds: dataToResponse?.groupIds || [],
                });
            }
            if (dataCcResponse?.groupIds?.length > 0) {
                newSendGroups.push({
                    type: MessageTargetType.Cc,
                    groupIds: dataCcResponse?.groupIds || [],
                });
            }
            if (dataBccResponse?.groupIds?.length > 0) {
                newSendGroups.push({
                    type: MessageTargetType.Bcc,
                    groupIds: dataBccResponse?.groupIds || [],
                });
            }

            let dataSubmit: IDateCreateMsg = {
                ...data,
                title: Helpers.getDataMultiLanguage({
                    data: data?.title,
                    currentLanguage: language,
                    listLanguage: listMultiLanguage,
                }),
                content: Helpers.getDataMultiLanguage({
                    data: data?.content,
                    currentLanguage: language,
                    listLanguage: listMultiLanguage,
                }),
                sendTime: sendTime,
                groups: newSendGroups,
                sendUsers: newSendUsers,
                clientId: data?.clientId || clientId,
                organizationId: data?.organizationId || userInfo?.userProfile?.organizationId || "0",
            };

            await NotificationService.createNotifi(dataSubmit);

            onSuccess(Strings.NOTIFICATION.CREATE_SUCCESS);

            props.onGoBack();

            onHideLoading();
        } catch (error) {
            onHideLoading();
            const e = Helpers.renderExceptionError(error);
            onError(e);
        }
    };

    const onChangeValue = (value: any, key: keyof IDateCreateMsg) => {
        let newState: any = { ...data };
        let newError: any = { ...error };

        newError[key] = undefined;

        switch (key) {
            case "title":
                newState[key] = {
                    ...newState[key],
                    value: {
                        ...newState[key]?.value,
                        [selectedLanguage]: value,
                    },
                };
                break;
            case "content":
                newState[key] = {
                    ...newState[key],
                    value: {
                        ...newState[key]?.value,
                        [selectedLanguage]: value,
                    },
                };
                break;
            case "type":
                newState[key] = value;
                newState["content"] = {
                    ...newState["content"],
                    value: {
                        [selectedLanguage]: "",
                    },
                };
                newError["content"] = undefined;
                break;
            case "realTime":
                newState[key] = value;
                newState["sendTime"] = moment().unix();
                newError["sendTime"] = undefined;
                break;
            default:
                newState[key] = value;
                break;
        }

        setIsEdit(true);
        setData(newState);
        setError(newError);
    };

    const basicCreateOrEdit = () => (
        <Grid container spacing={3}>
            <Grid item xs={12} md={props.mode === Mode.View ? 6 : 4}>
                <Autocomplete
                    required
                    isSelectedBox
                    mode={props.mode}
                    defaultValue={data?.type}
                    errorMessage={error?.type}
                    data={messageTypeList || []}
                    label={Strings.NOTIFICATION.NOTIFICATION_TYPE}
                    placeholder={Strings.NOTIFICATION.SELECT_NOTIFICATION_TYPE}
                    onChange={(value) => onChangeValue(value, "type")}
                />
            </Grid>
            <Grid item xs={12} md={props.mode === Mode.View ? 6 : 4}>
                <DateTimePicker
                    required
                    mode={props.mode}
                    options={{
                        time_24hr: true,
                        enableTime: true,
                        dateFormat: "d/m/Y H:i",
                    }}
                    disabled={data?.realTime}
                    errorMessage={error?.sendTime || ""}
                    value={getValueNumber1000(data?.sendTime)}
                    label={Strings.NOTIFICATION.SEND_TIME}
                    placeholder={Strings.NOTIFICATION.SELECT_SEND_TIME}
                    onChangeValue={(e: any) => {
                        onChangeValue(!Helpers.isNullOrEmpty(e) ? moment(e).unix() : undefined, "sendTime");
                    }}
                />
            </Grid>
            {props.mode !== Mode.View && (
                <Grid item xs={12} md={4} className="align-self-end">
                    <Typography variant="button" fontWeight="bold">
                        {Strings.NOTIFICATION.SEND_IMMEDIATELY}
                    </Typography>
                    <Switch checked={data?.realTime} onChange={(event, checked) => onChangeValue(checked, "realTime")} />
                </Grid>
            )}

            {/* To */}
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    isFullRow
                    mode={props.mode}
                    data={groupList || []}
                    defaultValue={dataToResponse?.groupIds || []}
                    label={Strings.NOTIFICATION.GROUP_RECEIVER}
                    placeholder={Strings.NOTIFICATION.SELECT_GROUP_RECEIVER}
                    onChange={(value) => {
                        setDataToResponse({ ...dataToResponse, groupIds: value });
                        setError((prev) => ({ ...prev, groups: "" }));
                        setIsEdit(true);
                    }}
                />
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    isFullRow
                    mode={props.mode}
                    data={userList || []}
                    label={Strings.NOTIFICATION.RECEIVER}
                    placeholder={Strings.NOTIFICATION.SELECT_RECEIVER}
                    required={dataToResponse?.groupIds?.length === 0}
                    defaultValue={(dataToResponse?.users || [])?.map((item) => item.userId)}
                    onChange={(value) => {
                        const newSendUsers = (value || []).map((id: string) => {
                            let itemUser = userList.find((user) => user.code === id);
                            return {
                                userId: itemUser?.code,
                                email: itemUser?.detail?.email,
                                phoneNumber: itemUser?.detail?.phoneNumber,
                            };
                        });

                        setDataToResponse({ ...dataToResponse, users: newSendUsers });
                        setError((prev) => ({ ...prev, sendUsers: "" }));
                        setIsEdit(true);
                    }}
                />
            </Grid>

            {/* CC */}
            {data?.type === MessageType.Email && (
                <Grid item xs={12}>
                    <Autocomplete
                        multiple
                        isFullRow
                        mode={props.mode}
                        data={groupList || []}
                        label={Strings.NOTIFICATION.GROUP_CC}
                        defaultValue={dataCcResponse?.groupIds || []}
                        placeholder={Strings.NOTIFICATION.SELECT_GROUP}
                        onChange={(value) => {
                            setDataCcResponse({ ...dataCcResponse, groupIds: value });
                            setIsEdit(true);
                        }}
                    />
                </Grid>
            )}
            {data?.type === MessageType.Email && (
                <Grid item xs={12}>
                    <Autocomplete
                        multiple
                        isFullRow
                        mode={props.mode}
                        data={userList || []}
                        label={Strings.NOTIFICATION.USER_CC}
                        placeholder={Strings.NOTIFICATION.SELECT_USER}
                        required={dataCcResponse?.groupIds?.length === 0}
                        defaultValue={(dataCcResponse?.users || [])?.map((item) => item.userId)}
                        onChange={(value) => {
                            const newSendUsers = (value || []).map((id: string) => {
                                let itemUser = userList.find((user) => user.code === id);
                                return {
                                    userId: itemUser?.code,
                                    email: itemUser?.detail?.email,
                                    phoneNumber: itemUser?.detail?.phoneNumber,
                                };
                            });
                            setDataCcResponse({ ...dataCcResponse, users: newSendUsers });
                            setIsEdit(true);
                        }}
                    />
                </Grid>
            )}

            {/* BCC */}
            {data?.type === MessageType.Email && (
                <Grid item xs={12}>
                    <Autocomplete
                        multiple
                        isFullRow
                        mode={props.mode}
                        data={groupList || []}
                        label={Strings.NOTIFICATION.GROUP_BCC}
                        defaultValue={dataBccResponse?.groupIds || []}
                        placeholder={Strings.NOTIFICATION.SELECT_GROUP}
                        onChange={(value) => {
                            setDataBccResponse({ ...dataBccResponse, groupIds: value });
                            setIsEdit(true);
                        }}
                    />
                </Grid>
            )}
            {data?.type === MessageType.Email && (
                <Grid item xs={12}>
                    <Autocomplete
                        multiple
                        isFullRow
                        mode={props.mode}
                        data={userList || []}
                        label={Strings.NOTIFICATION.USER_BCC}
                        placeholder={Strings.NOTIFICATION.SELECT_USER}
                        required={dataBccResponse?.groupIds?.length === 0}
                        defaultValue={(dataBccResponse?.users || [])?.map((item) => item.userId)}
                        onChange={(value) => {
                            const newSendUsers = (value || []).map((id: string) => {
                                let itemUser = userList.find((user) => user.code === id);
                                return {
                                    userId: itemUser?.code,
                                    email: itemUser?.detail?.email,
                                    phoneNumber: itemUser?.detail?.phoneNumber,
                                };
                            });
                            setDataBccResponse({ ...dataBccResponse, users: newSendUsers });
                            setIsEdit(true);
                        }}
                    />
                </Grid>
            )}

            <Grid item xs={12}>
                <FormField
                    required
                    multiline
                    isFullRow
                    maxLength={255}
                    mode={props.mode}
                    errorMessage={error.title || ""}
                    label={Strings.NOTIFICATION.TITLE}
                    placeholder={Strings.NOTIFICATION.ENTER_TITLE}
                    value={data?.title?.value?.[selectedLanguage] || ""}
                    onChangeValue={(value) => onChangeValue(value, "title")}
                />
            </Grid>

            <Grid item xs={12}>
                {data?.type === MessageType.Email ? (
                    <InputHtmlEditor
                        required
                        mode={props.mode}
                        readonly={props.mode === Mode.View}
                        label={Strings.NOTIFICATION.CONTENT}
                        placeholder={Strings.NOTIFICATION.ENTER_CONTENT}
                        value={data?.content?.value?.[selectedLanguage] || ""}
                        errorMessage={error.content || ""}
                        onBlur={(value) => onChangeValue(value, "content")}
                    />
                ) : (
                    <FormField
                        required
                        multiline
                        isFullRow
                        minRows={5}
                        maxLength={255}
                        mode={props.mode}
                        label={Strings.NOTIFICATION.CONTENT}
                        placeholder={Strings.NOTIFICATION.ENTER_CONTENT}
                        errorMessage={error.content || ""}
                        value={data?.content?.value?.[selectedLanguage] || ""}
                        onChangeValue={(value) => onChangeValue(value, "content")}
                    />
                )}
            </Grid>
        </Grid>
    );

    return (
        <>
            <Box>
                <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "end", marginBottom: 3 }}>
                    <Button
                        color="secondary"
                        onClick={() => {
                            if (isEdit) {
                                Helpers.showConfirmAlert(Strings.Common.CANCEL_ACTION_MESSAGE, props.onGoBack);
                            } else {
                                props.onGoBack();
                            }
                        }}
                    >
                        {Strings.Common.GO_BACK}
                    </Button>
                    {props.mode === Mode.Create && (
                        <Button
                            sx={{ ml: 2 }}
                            color="dark"
                            onClick={() => {
                                handleSubmit();
                            }}
                        >
                            {Strings.Common.SAVE}
                        </Button>
                    )}
                </Box>
                <Card>
                    <Box p={2}>
                        <Box>
                            <Typography variant="h5">{Strings.NOTIFICATION.BASIC_INFO}</Typography>
                            <SelectMultiLanguage
                                defaultLanguage={selectedLanguage}
                                onChangeLanguage={(value) => {
                                    setSelectedLanguage(value);
                                    setError({
                                        ...error,
                                        title: "",
                                        content: "",
                                    });
                                }}
                            />
                        </Box>
                        <Box component="form" p={2}>
                            {basicCreateOrEdit()}
                        </Box>
                    </Box>
                </Card>
            </Box>
        </>
    );
};

export default NotificationEditContainer;
